import { useCallback, useEffect, useState } from 'react';
import { profilesAPI, usersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Mail, Pencil, Plus, Trash2, UserCog, Loader2 } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'manager', label: 'Manager — view dashboard, no Amazon connect' },
  { value: 'client', label: 'Client' },
  { value: 'recovery-specialist', label: 'Recovery specialist' },
  { value: 'admin', label: 'Admin' },
];

const emptyForm = () => ({
  name: '',
  email: '',
  password: '',
  role: 'manager',
  profile_ids: [],
  send_invite: false,
});

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [invitingId, setInvitingId] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await usersAPI.list({ search: debouncedSearch, role: roleFilter || undefined, page_size: 100 });
      setUsers(res.data?.data || []);
    } catch (err) {
      const message = err.code === 'ECONNABORTED'
        ? 'Request timed out — is the backend running on port 8000?'
        : (err.response?.data?.message || 'Failed to load users');
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, roleFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    profilesAPI.listAll()
      .then((res) => setProfiles(res.data?.data || res.data || []))
      .catch(() => setProfiles([]));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = async (user) => {
    try {
      const res = await usersAPI.get(user.id);
      const data = res.data?.data || user;
      const profileIds = (res.data?.profile_ids || []).map(String);
      setEditing(user);
      setForm({
        name: data.name || '',
        email: data.email || '',
        password: '',
        role: data.role || 'manager',
        profile_ids: profileIds,
        send_invite: false,
      });
      setDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load user');
    }
  };

  const toggleProfile = (profileId) => {
    const id = String(profileId);
    setForm((prev) => ({
      ...prev,
      profile_ids: prev.profile_ids.includes(id)
        ? prev.profile_ids.filter((p) => p !== id)
        : [...prev.profile_ids, id],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (!editing && !form.send_invite && !form.password) {
      toast.error('Password is required when not using a set-password link');
      return;
    }
    setSaving(true);
    try {
      const profileIds = form.profile_ids.map((id) => Number(id));
      if (editing) {
        const payload = { name: form.name, profile_ids: profileIds };
        if (form.password) payload.password = form.password;
        await usersAPI.update(editing.id, payload);
        toast.success('User updated');
      } else {
        await usersAPI.create({
          name: form.name,
          email: form.email,
          role: form.role,
          password: form.password || undefined,
          profile_ids: profileIds,
          send_invite: form.send_invite,
        });
        toast.success('User created — welcome email sent with login details');
      }
      setDialogOpen(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.name}?`)) return;
    try {
      await usersAPI.remove(user.id);
      toast.success('User deleted');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleInvite = async (user) => {
    if (invitingId) return;
    setInvitingId(user.id);
    const toastId = toast.loading(`Sending welcome email to ${user.email}…`);
    try {
      const res = await usersAPI.sendInvite(user.id);
      toast.success(res.data?.message || 'Welcome email sent', { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invite', { id: toastId });
    } finally {
      setInvitingId(null);
    }
  };

  const showBrandPicker = editing
    ? ['manager', 'client'].includes(editing.role)
    : ['manager', 'client'].includes(form.role);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <UserCog className="w-5 h-5" /> Team & managers
          </h1>
          <p className="text-sm text-muted-foreground">Create managers, assign brands (profiles), send login invites</p>
        </div>
        <Button onClick={openCreate} className="grad-accent text-white border-0 gap-2">
          <Plus className="w-4 h-4" /> Add user
        </Button>
      </div>

      <Card className="bg-card border-border/80">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap gap-3">
            <Input
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs h-9"
            />
            <Select value={roleFilter || 'all'} onValueChange={(v) => setRoleFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40 h-9">
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label.split(' —')[0]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : loadError ? (
            <div className="py-8 text-center space-y-3">
              <p className="text-sm text-destructive">{loadError}</p>
              <Button variant="outline" size="sm" onClick={loadUsers}>Retry</Button>
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No users found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Brands</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{u.role}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                      {(u.profile_labels || u.profile_names || []).join(', ') || '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      {u.role !== 'admin' && u.role !== 'superadmin' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={invitingId === u.id}
                            onClick={() => handleInvite(u)}
                            title="Resend welcome email with set-password link"
                          >
                            {invitingId === u.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(u)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(u)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit user' : 'Add user'}</DialogTitle>
            <DialogDescription>
              A welcome email is always sent with assigned brands. By default it includes email and password; optionally use a set-password link instead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </div>
            {!editing && (
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!editing && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send_invite"
                  checked={form.send_invite}
                  onCheckedChange={(c) => setForm((p) => ({ ...p, send_invite: !!c }))}
                />
                <Label htmlFor="send_invite" className="text-sm font-normal">
                  Send set-password link instead of password in welcome email
                </Label>
              </div>
            )}
            {(!form.send_invite || editing) && (
              <div className="space-y-2">
                <Label>{editing ? 'New password (optional)' : 'Password (included in welcome email)'}</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder={editing ? 'Leave blank to keep current' : 'Min 8 characters'}
                />
              </div>
            )}
            {showBrandPicker && (
              <div className="space-y-2">
                <Label>Assigned brands (profiles)</Label>
                <div className="max-h-48 overflow-y-auto border border-border/60 rounded-lg p-3 space-y-2">
                  {profiles.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No profiles available</p>
                  ) : (
                    profiles.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={form.profile_ids.includes(String(p.id))}
                          onCheckedChange={() => toggleProfile(p.id)}
                        />
                        <span>{p.name || `Profile ${p.id}`}{p.countryCode ? ` (${p.countryCode})` : ''}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="grad-accent text-white border-0">
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Create user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
